/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import { compose } from 'recompose';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import deferToClientSideRender from 'js/lib/deferToClientSideRender';
import { useRetracked } from 'js/lib/retracked';
import user from 'js/lib/user';

import {
  Button,
  Container,
  Grid,
  Skeleton,
  Tab,
  TabContext,
  TabPanel,
  Tabs,
  Typography,
  Typography2,
  breakpoints,
} from '@coursera/cds-core';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import CourseraPlusEnrollButton from 'bundles/coursera-plus-landing-page/components/CourseraPlusEnrollButton';
import type { PropsForEnrollPresentation } from 'bundles/coursera-plus-landing-page/components/CourseraPlusEnrollButton';
import PriceComparisonBlock from 'bundles/coursera-plus-landing-page/components/priceComparison/PriceComparisonBlock';
import {
  getCoursePriceBlock,
  getMainTitle,
  getMonthlyPriceBlock,
  getWesHero,
  getWesPriceBlock,
  getYearlyPriceBlock,
} from 'bundles/coursera-plus-landing-page/components/priceComparison/PriceComparisonCopies';
import type { BlockContent } from 'bundles/coursera-plus-landing-page/components/priceComparison/PriceComparisonCopies';
import { useLandingPageExtended } from 'bundles/coursera-plus-landing-page/contentfulData/useLandingPageData';
import {
  isWesOnCourseraPlusLandingPageEnabled,
  useEnterpriseContractPrice,
} from 'bundles/coursera-plus-landing-page/utils/wesOnCourseraPlusLandingPageUtils';
import { ENROLL_BUTTON_TESTID } from 'bundles/coursera-plus/components/constants';
import type { SubscriptionTiersProductVariant } from 'bundles/coursera-plus/constants/CourseraPlusProductVariant';
import CourseraPlusProductVariants from 'bundles/coursera-plus/constants/CourseraPlusProductVariant';
import type { PropsFromCourseraPlusProductOwnerships } from 'bundles/coursera-plus/utils/withCourseraPlusProductOwnerships';
import withCourseraPlusProductOwnerships from 'bundles/coursera-plus/utils/withCourseraPlusProductOwnerships';
import courseraPlusEpicClient from 'bundles/epic/clients/courseraPlus';
import { useNaptime } from 'bundles/naptimejs';
import S12nDerivativesV1 from 'bundles/naptimejs/resources/s12nDerivatives.v1';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import ReactPriceDisplay from 'bundles/payments-common/components/ReactPriceDisplay';
import ApiButton from 'bundles/red-shibas-cds/components/ApiButton';

import _t from 'i18n!nls/coursera-plus-landing-page';

const COLUMN_WIDTH_WIDE = 359;
const GUTTER_WIDE = 0;
const COLUMN_WIDTH_NARROW = 290;
const GUTTER_NARROW = 14;

const useStyles = () => {
  return {
    wrapper: css`
      background-color: var(--cds-color-emphasis-primary-background-xweak);
      width: 100%;
    `,
    scroll: css`
      overflow-x: auto;
      padding-bottom: 120px;
    `,
    gridWrapper: css`
      width: ${3 * COLUMN_WIDTH_WIDE + 2 * GUTTER_WIDE}px;
      margin: 0 auto;
      ${breakpoints.down('lg')} {
        width: ${3 * COLUMN_WIDTH_NARROW + 2 * GUTTER_NARROW}px;
      }
    `,
    gridContainer: css`
      display: grid;
      grid-column-gap: ${GUTTER_WIDE};
      grid-template-columns: auto auto auto;
      ${breakpoints.down('lg')} {
        grid-column-gap: ${GUTTER_NARROW};
      }
    `,
    gridItem: css`
      width: ${COLUMN_WIDTH_WIDE - GUTTER_NARROW}px;
      ${breakpoints.down('lg')} {
        width: ${COLUMN_WIDTH_NARROW}px;
      }
    `,
    mainTitle: css`
      text-align: center;
      padding-top: var(--cds-spacing-800);
      padding-bottom: var(--cds-spacing-600);

      ${breakpoints.down('md')} {
        padding: var(--cds-spacing-600) var(--cds-spacing-300);
      }
    `,
    priceNumber: css`
      font-weight: 700;
      font-size: 34px;
    `,
    priceSubText: css`
      margin: 8px 0;
    `,
    priceQualifier: css`
      font-weight: 700;
      font-size: 16px;
    `,
    actionButton: css`
      margin: 17px auto 10px auto;
      width: ${COLUMN_WIDTH_WIDE - 80}px;
      padding: 12px 16px !important;
      font-weight: 700 !important;
      font-size: 14px !important;
      line-height: 24px !important;
      ${breakpoints.down('lg')} {
        width: ${COLUMN_WIDTH_NARROW - 40}px;
      }
    `,
    noActionButton: css`
      background-color: transparent;
      border-color: transparent;
      font-weight: normal;
    `,
    enrollAndPrice: css`
      text-align: center;
    `,
    wesHeroTitle: css`
      padding-bottom: 16px;
    `,
    wesHeroDescription: css`
      ${breakpoints.down('sm')} {
        font-size: 16px;
      }
    `,
    wesHeroContainer: css`
      ${breakpoints.up('sm')} {
        max-width: 80%;
      }
    `,
    wesContainer: css`
      padding: 0 10px 120px;
    `,
    tabsContainer: css`
      .cds-tab-list-scrollable {
        display: flex !important;
        justify-content: center !important;
        padding: 10px;

        [role='tablist'] {
          width: 100%;

          button {
            width: 50%;
            margin: 0;
            max-width: none !important;

            &:hover {
              border-radius: 100px;
            }

            &:focus {
              outline: none;
            }
          }
        }
      }

      button.cds-tab-selected {
        border-radius: 100px;
        background: var(--cds-color-blue-700);
        color: white !important;
        width: 50%;
        margin: 0;
      }

      .cds-tab-list-indicator {
        display: none;
      }

      border-bottom: none;
      max-width: 450px;
      margin: 0 auto;
      border-radius: 100px;
      background: white;
      margin-bottom: var(--cds-spacing-800);
    `,
    wesActionButton: css`
      margin: 0 auto;
      width: ${COLUMN_WIDTH_WIDE - 80}px;
      padding: 10px !important;
      font-weight: 700 !important;
      font-size: 14px !important;
      line-height: 24px !important;
      ${breakpoints.down('lg')} {
        width: ${COLUMN_WIDTH_NARROW - 40}px;
      }

      a:hover {
        text-decoration: none;
      }
    `,
    discountPercentTag: css`
      margin-left: 8px;

      & > span {
        font-weight: 700 !important;
      }
    `,
    monthlyBlock: css`
      display: block !important;
      margin-bottom: 40px !important;
    `,
    fontWeight600: css`
      font-weight: 600;
    `,
    teamsPriceLoader: css`
      display: flex;
      justify-content: center;
    `,
  };
};

type PropsFromCaller = {
  monthlyProductId?: SubscriptionTiersProductVariant;
  annualProductId?: SubscriptionTiersProductVariant;
};

type PropsToPriceComponent = {
  price: JSX.Element;
  monthly: boolean;
};

type PropsFromNaptimeS12nDerivatives = {
  derivativesFrom?: S12nDerivativesV1;
  derivativesTo?: S12nDerivativesV1;
};

type PropsToComponent = PropsFromCaller & PropsFromNaptimeS12nDerivatives & PropsFromCourseraPlusProductOwnerships;

const MONTHLY_TRACKING = 'monthly_from_price_comparison';
const YEARLY_TRACKING = 'yearly_from_price_comparison';

const TrackedEnrollButton = withSingleTracked({ type: 'BUTTON' })(ApiButton);

const Price = (props: PropsToPriceComponent) => {
  const styles = useStyles();
  const { price, monthly } = props;
  const qualifier = monthly ? _t('month') : _t('year');
  const styledPrice = (
    <Typography2 component="span" variant="inherit" css={styles.priceNumber}>
      {price}
    </Typography2>
  );
  const styledQualifier = (
    <Typography2 component="span" variant="bodyPrimary" css={styles.priceQualifier}>
      {qualifier}
    </Typography2>
  );
  return <FormattedMessage message="{price} /{qualifier}" price={styledPrice} qualifier={styledQualifier} />;
};

const FromToPrices = (props: PropsToComponent) => {
  const { derivativesFrom, derivativesTo } = props;
  const getPrice = (src: S12nDerivativesV1 | undefined): JSX.Element | null => {
    if (!src) {
      return null;
    }
    if (!src.catalogPrice) {
      return null;
    }
    return (
      <Typography2 component="span" variant="inherit">
        <ReactPriceDisplay
          value={src.catalogPrice.amount}
          currency={src.catalogPrice.currencyCode}
          hideCurrencyCode={true}
        />
      </Typography2>
    );
  };
  const fromPrice = getPrice(derivativesFrom);
  const toPrice = getPrice(derivativesTo);

  if (!fromPrice || !toPrice) {
    return null;
  }
  return (
    <Typography2 component="span" variant="inherit">
      {fromPrice}&nbsp;&ndash;&nbsp;
      {toPrice}
    </Typography2>
  );
};

const EnrollAndPrice = (props: PropsForEnrollPresentation) => {
  const styles = useStyles();
  const { price, apiStatus, trackingName, onClick, ownsCourseraLite } = props;
  const { courseraPlusAnnualWithFreeTrialEpicVariant, userHasCourseraPlusFreeTrial } = useLandingPageExtended();
  const isAnnualWithFreeTrialEpicTestVariant = ['1', '2', '3'].includes(courseraPlusAnnualWithFreeTrialEpicVariant);

  const monthly = trackingName === MONTHLY_TRACKING;
  const block: BlockContent = monthly
    ? getMonthlyPriceBlock({ trialAvailable: userHasCourseraPlusFreeTrial, ownsCourseraLite })
    : getYearlyPriceBlock({
        isAnnualWithFreeTrialEpicTestVariant,
        userHasCourseraPlusFreeTrial,
        ownsCourseraLite: Boolean(ownsCourseraLite),
      });

  const renderPrice = (price: JSX.Element | null) => {
    if (!price) {
      return null;
    }

    return (
      <Grid>
        <Price price={price} monthly={monthly} />
      </Grid>
    );
  };
  return (
    <div css={styles.enrollAndPrice}>
      {renderPrice(price)}
      <TrackedEnrollButton
        trackingName={trackingName}
        css={styles.actionButton}
        label={block.actionLabel}
        onClick={onClick}
        variant={monthly ? 'primary' : 'secondary'}
        size="medium"
        apiStatus={apiStatus}
        data-e2e={ENROLL_BUTTON_TESTID}
      />
    </div>
  );
};

const TO_S12N_ID = 'RM8asLB_Eeq8swrFx8pPPQ';
const FROM_S12N_ID = 'ZIBOPMDUEei-xRJApul-jA';

type TabValue = 'individuals' | 'teams';

const PriceComparison = (props: PropsToComponent) => {
  const styles = useStyles();
  const { ownsCourseraPlus, monthlyProductId, annualProductId, ownsCourseraLite } = props;
  const {
    courseraPlusAnnualWithFreeTrialProduct,
    courseraPlusAnnualWithFreeTrialEpicVariant,
    userHasCourseraPlusFreeTrial,
  } = useLandingPageExtended();
  const [tabValue, setTabValue] = useState<TabValue>('individuals');
  const centeredElement = useRef<HTMLDivElement>(null);
  const scrollElement = useRef<HTMLDivElement>(null);
  const trackComponent = useRetracked();
  const timeout = useRef(0);

  const {
    data: { derivativesFrom, derivativesTo },
  } = useNaptime<PropsFromNaptimeS12nDerivatives>(
    () => ({
      derivativesTo: S12nDerivativesV1.get(TO_S12N_ID, {
        fields: ['catalogPrice'],
      }),
      derivativesFrom: S12nDerivativesV1.get(FROM_S12N_ID, {
        fields: ['catalogPrice'],
      }),
    }),
    []
  );

  // Only fetch price when Teams tab becomes active for the first time
  const { enterpriseContractPrice } = useEnterpriseContractPrice({
    skipApiCall: tabValue !== 'teams',
  });

  useEffect(() => {
    // Record impression for WES on CourseraPlus experiment(Rollout) https://tools.coursera.org/epic/experiment/C-Ph2gdwEe-nlw5rS2MojQ
    courseraPlusEpicClient.get('showWesOnCourseraPlusLandingPage');

    const centerScroll = () => {
      const blockWidth = centeredElement.current?.clientWidth ?? 0;
      const windowWidth = window.innerWidth;
      if (windowWidth < blockWidth && scrollElement.current) {
        scrollElement.current.scrollLeft = (blockWidth - windowWidth) / 2;
      }
    };

    const debouncedScroll = () => {
      window.clearTimeout(timeout.current);
      timeout.current = window.setTimeout(centerScroll, 500);
    };

    window.setTimeout(centerScroll);
    window.addEventListener('resize', debouncedScroll);
    return () => {
      window.removeEventListener('resize', debouncedScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (ownsCourseraPlus) {
    return null;
  }

  const isWesOnLandingPageEnabled = isWesOnCourseraPlusLandingPageEnabled();
  const wesBlock = isWesOnLandingPageEnabled ? getWesPriceBlock() : undefined;
  const wesHero = isWesOnLandingPageEnabled ? getWesHero() : undefined;

  const isAnnualWithFreeTrialEpicTestVariant = ['1', '2', '3'].includes(courseraPlusAnnualWithFreeTrialEpicVariant);
  const courseBlock = getCoursePriceBlock();
  const monthlyBlock = getMonthlyPriceBlock({});
  const yearlyBlock = getYearlyPriceBlock({
    isAnnualWithFreeTrialEpicTestVariant,
    userHasCourseraPlusFreeTrial,
    ownsCourseraLite: Boolean(ownsCourseraLite),
  });

  let finalCourseraPlusAnnualProductId;
  if (isAnnualWithFreeTrialEpicTestVariant) {
    finalCourseraPlusAnnualProductId =
      courseraPlusAnnualWithFreeTrialProduct?.productItemId as SubscriptionTiersProductVariant;
  } else {
    finalCourseraPlusAnnualProductId = annualProductId ?? CourseraPlusProductVariants.ANNUAL_NO_FREE_TRIAL;
  }

  const handleTabClick = (value: TabValue) => {
    setTabValue(value);
    trackComponent({
      // eslint-disable-next-line camelcase
      trackingData: { user_id: user.get()?.id },
      trackingName: `cplus_price_comparison_tab_${value}`,
      action: `click_tab`,
    });
  };

  const consumerContent = (
    <div css={styles.scroll} ref={scrollElement}>
      <div css={styles.gridWrapper} ref={centeredElement}>
        <div css={styles.gridContainer}>
          <div css={styles.gridItem}>
            <PriceComparisonBlock content={courseBlock}>
              <Price
                price={<FromToPrices {...props} derivativesFrom={derivativesFrom} derivativesTo={derivativesTo} />}
                monthly={true}
              />
              <Typography2 component="p" css={[styles.actionButton, styles.noActionButton]}>
                {courseBlock.actionLabel}
              </Typography2>
            </PriceComparisonBlock>
          </div>
          <div css={styles.gridItem}>
            <PriceComparisonBlock isMain={true} content={monthlyBlock}>
              <CourseraPlusEnrollButton
                productItemId={monthlyProductId ?? CourseraPlusProductVariants.MONTHLY_WITH_FREE_TRIAL}
                trackingName={MONTHLY_TRACKING}
                PresentationComponent={EnrollAndPrice}
              />
            </PriceComparisonBlock>
          </div>
          <div css={styles.gridItem}>
            <PriceComparisonBlock content={yearlyBlock}>
              <CourseraPlusEnrollButton
                productItemId={finalCourseraPlusAnnualProductId}
                trackingName={YEARLY_TRACKING}
                PresentationComponent={EnrollAndPrice}
              />
            </PriceComparisonBlock>
          </div>
        </div>
      </div>
    </div>
  );

  const wesContent = wesBlock && wesHero && (
    <Container maxWidth="md" css={styles.wesContainer}>
      <Grid container justifyContent="space-around">
        <Grid container lg={5} xs={12} justifyContent="center" direction="column" css={styles.wesHeroContainer}>
          <Typography variant="d2" css={styles.wesHeroTitle}>
            {wesHero.title}
          </Typography>
          <Typography variant="h2" color="supportText" css={styles.wesHeroDescription}>
            {wesHero.description}
          </Typography>
        </Grid>
        <Grid lg={4}>
          <PriceComparisonBlock content={wesBlock} isActive={tabValue === 'teams'}>
            <>
              {!enterpriseContractPrice ? (
                <div data-testid="teams-price-loader" css={styles.teamsPriceLoader}>
                  <Skeleton variant="rect" width={111} height={30} />
                </div>
              ) : (
                <Price
                  price={
                    <ReactPriceDisplay
                      value={Number(enterpriseContractPrice.amount)}
                      currency={enterpriseContractPrice.currencyCode}
                      hideCurrencyCode={true}
                    />
                  }
                  monthly={false}
                />
              )}
            </>
            <div>{wesBlock.priceLabel}</div>
            <div css={styles.wesActionButton}>
              <Button
                variant="primary"
                fullWidth
                component={TrackedLink2}
                trackingName="wes_on_cplus_getstarted_button"
                target="_blank"
                href={wesBlock.actionLink}
              >
                {wesBlock.actionLabel}
              </Button>
            </div>
          </PriceComparisonBlock>
        </Grid>
      </Grid>
    </Container>
  );

  return (
    <div data-testid="price-comparison" css={styles.wrapper}>
      <TrackedDiv
        trackingName="cplus_landing_page_price_comparison"
        withVisibilityTracking={true}
        requireFullyVisible
        data={{ userId: user.get()?.id }}
      >
        <Typography2 component="h2" variant="titleSmall" css={styles.mainTitle}>
          {getMainTitle()}
        </Typography2>
      </TrackedDiv>
      {isWesOnLandingPageEnabled ? (
        <TabContext value={tabValue}>
          <Tabs variant="section" aria-label={_t('Price comparison tab')} css={styles.tabsContainer}>
            <Tab
              label={_t('For Individuals')}
              onClick={() => {
                handleTabClick('individuals');
              }}
              value="individuals"
            />

            <Tab
              label={_t('For Teams')}
              onClick={() => {
                handleTabClick('teams');
              }}
              value="teams"
            />
          </Tabs>
          <TabPanel value="individuals">{consumerContent}</TabPanel>
          <TabPanel value="teams">{wesContent}</TabPanel>
        </TabContext>
      ) : (
        consumerContent
      )}
    </div>
  );
};

export default compose<PropsFromNaptimeS12nDerivatives, PropsFromCaller>(
  deferToClientSideRender,
  withCourseraPlusProductOwnerships<PropsFromCaller>()
)(PriceComparison);
